// ═══════════════════════════════════════════════════════════════════════════
// SEDI E SITI DEL CLIENTE — regole pure (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Decisione di Paolo (ott 2026): gli indirizzi dove si lavora sono una caratteristica del
// CLIENTE, non della singola commessa. In anagrafica cliente si registrano le sue sedi
// (legale, operativa, impianto, cantiere, abitazione, altro), con referente in loco e
// accessi. Commessa, offerta, rapportino, sopralluogo, DDT, impianti… scelgono la sede
// dall'elenco e ne salvano una COPIA (snapshot): se domani la sede cambia, un documento già
// emesso resta com'era. Se la sede non c'è, si crea lì e finisce nell'anagrafica.
// Dove sono salvate: clienti/{id}.sedi = [{ id, nome, tipo, indirizzo (via), civico, cap, comune,
// provincia, referente, telefono, orari, accesso, note, attiva }]. L'indirizzo storico del
// cliente (campo "indirizzo") vale come sede legale finché non se ne registra una.
// ═══════════════════════════════════════════════════════════════════════════

const norm = (t) => String(t || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export const TIPI_SEDE = {
  legale:     { id: "legale",     label: "Sede legale" },
  operativa:  { id: "operativa",  label: "Sede operativa" },
  impianto:   { id: "impianto",   label: "Impianto" },
  cantiere:   { id: "cantiere",   label: "Cantiere" },
  abitazione: { id: "abitazione", label: "Abitazione" },
  altro:      { id: "altro",      label: "Altro" },
};

export const SEDE_VUOTA = { nome: "", tipo: "operativa", indirizzo: "", civico: "", cap: "", comune: "", provincia: "", referente: "", telefono: "", orari: "", accesso: "", note: "", attiva: true };

export const nuovoIdSede = () => "sede_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

/** Indirizzo su una riga: "Via Roma 1, 40100 Bologna (BO)". */
export function indirizzoCompleto(s) {
  if (!s) return "";
  const luogo = [s.cap, s.comune].filter(x => String(x || "").trim()).join(" ") + (String(s.provincia || "").trim() ? ` (${String(s.provincia).trim().toUpperCase()})` : "");
  const via = [String(s.indirizzo || "").trim(), String(s.civico || "").trim()].filter(Boolean).join(" ");
  return [via, luogo.trim()].filter(Boolean).join(", ");
}

/** Sedi del cliente da proporre: la legale (anche solo dal vecchio campo indirizzo) e le attive. */
export function sediDelCliente(cliente) {
  if (!cliente) return [];
  const tutte = (Array.isArray(cliente.sedi) ? cliente.sedi : []).filter(Boolean);
  const sedi = tutte.filter(s => s.attiva !== false);
  // se una sede legale è registrata (anche disattivata) il vecchio campo indirizzo non si propone più
  const haLegale = tutte.some(s => s.tipo === "legale");
  const legaleStorica = !haLegale && String(cliente.indirizzo || "").trim()
    ? [{ id: "legale", nome: "Sede legale", tipo: "legale", indirizzo: String(cliente.indirizzo).trim(), referente: cliente.referente || "", telefono: cliente.telefono || "", virtuale: true }]
    : [];
  return [...legaleStorica, ...sedi];
}

/**
 * La sede dell'elenco che corrisponde alla copia salvata in un documento: per id, oppure
 * (per la sede legale ricavata dal vecchio indirizzo, o una sede ricreata) per indirizzo.
 */
export function sedeCorrispondente(sedi = [], copia) {
  if (!copia) return null;
  if (copia.sedeId) { const s = sedi.find(x => x.id === copia.sedeId); if (s) return s; }
  if (!copia.sedeId && copia.tipo === "altro") return null; // indirizzo scritto a mano
  const n = norm(copia.indirizzo);
  if (!n) return null;
  return sedi.find(s => norm(indirizzoCompleto(s)) === n || norm(s.indirizzo) === n) || null;
}

/** Copia della sede da salvare dentro un documento. */
export function copiaSede(s) {
  if (!s) return null;
  return {
    sedeId: s.id || "", nome: s.nome || TIPI_SEDE[s.tipo]?.label || "", tipo: s.tipo || "",
    indirizzo: indirizzoCompleto(s), referente: s.referente || "", telefono: s.telefono || "",
    orari: s.orari || "", accesso: s.accesso || "", note: s.note || "",
  };
}

/**
 * Sede dei lavori di una commessa: quella indicata sulla commessa; altrimenti, se il cliente
 * ha una sola sede (anche solo l'indirizzo dell'anagrafica, che vale come sede legale), è
 * quella — non serve indicarla. Solo con più sedi bisogna scegliere dove si lavora.
 * → { sede, implicita, nSedi }
 */
export function sedeLavori(project, cliente) {
  if (project?.sede && (project.sede.indirizzo || project.sede.nome)) return { sede: project.sede, implicita: false, nSedi: null };
  if (String(project?.sitoCliente || "").trim()) return { sede: copiaLibera(project.sitoCliente), implicita: false, nSedi: null };
  const sedi = sediDelCliente(cliente);
  return sedi.length === 1 ? { sede: copiaSede(sedi[0]), implicita: true, nSedi: 1 } : { sede: null, implicita: false, nSedi: sedi.length };
}

/** Copia per un indirizzo scritto a mano (non salvato nell'anagrafica). */
export const copiaLibera = (testo) => ({ sedeId: "", nome: "", tipo: "altro", indirizzo: String(testo || "").trim(), referente: "", telefono: "", orari: "", accesso: "", note: "" });

/** Una riga leggibile: "Stabilimento di Bologna — Via Roma 1, Bologna". */
export function descrizioneSede(copia) {
  if (!copia) return "";
  const nome = String(copia.nome || "").trim(), ind = String(copia.indirizzo || "").trim();
  if (nome && ind && nome !== ind) return `${nome} — ${ind}`;
  return nome || ind;
}

/** Link a Google Maps per un indirizzo (null se vuoto). */
export function linkMappa(indirizzo) {
  const t = String(indirizzo || "").trim();
  return t ? "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(t) : null;
}

/** Link per telefonare (null se non sembra un numero). */
export function linkTelefono(numero) {
  const t = String(numero || "").replace(/[^\d+]/g, "");
  return t.length >= 6 ? "tel:" + t : null;
}

/** La sede che corrisponde a un indirizzo scritto (stesso testo, ignorando spazi e punteggiatura). */
export function trovaSedePerIndirizzo(cliente, testo) {
  const n = norm(testo);
  if (!n) return null;
  return sediDelCliente(cliente).find(s => norm(indirizzoCompleto(s)) === n || norm(s.indirizzo) === n || norm(s.nome) === n) || null;
}

/**
 * Indirizzi già usati per questo cliente (impianti, offerte, commesse) che non sono ancora
 * sedi registrate: si propongono, raggruppando quelli uguali, e si aggiungono solo con conferma.
 * fonti: [{ testo, nome, origine }]
 */
export function proposteSedi(cliente, fonti = []) {
  const gia = new Set(sediDelCliente(cliente).flatMap(s => [norm(indirizzoCompleto(s)), norm(s.indirizzo)]).filter(Boolean));
  const viste = new Map();
  fonti.forEach(f => {
    const n = norm(f.testo);
    if (!n || n.length < 6 || gia.has(n)) return;
    if (!viste.has(n)) viste.set(n, { indirizzo: String(f.testo).trim(), nome: f.nome || "", origini: [] });
    const v = viste.get(n);
    if (!v.nome && f.nome) v.nome = f.nome;
    if (f.origine && !v.origini.includes(f.origine)) v.origini.push(f.origine);
  });
  return [...viste.values()];
}

/**
 * Chi può aggiungere o modificare le sedi dei clienti (decisione di Paolo, ott 2026):
 * titolare, amministratori e i ruoli Gestione aziendale (ufficio), Amministrazione, Project
 * Manager. Stessa regola nelle Regole Firestore (puoGestireSediClienti).
 */
export const RUOLI_SEDI = ["gestione", "amministrazione", "pm"];
export function puoGestireSedi({ isPlatformOwner, isOrgAdmin, userProfile } = {}) {
  return !!(isPlatformOwner || isOrgAdmin || ["admin", "titolare"].includes(userProfile?.role) || RUOLI_SEDI.includes(userProfile?.ruoloOperativo));
}
